import { ref, Ref } from 'vue';
import { defaultConfig } from '@/cms/api/ApiConfig';
import { getCurrentReturnUrl, isLocalReturnUrl, TOKEN_EXPIRE_ENDPOINT } from './authUtilities';
import { AuthStatus } from './types';

export interface UseAuthStatusResult {
	/** The latest result, or null before the first check. */
	status: Ref<AuthStatus | null>;
	loading: Ref<boolean>;
	/** Message from the latest failed check, otherwise null. */
	error: Ref<string | null>;
	/**
	 * Asks the CMS whether the user is logged in. The CMS picks the cookie scheme from the host.
	 * `returnUrl` is a local path to come back to after login, e.g. `/calculator?step=3`; it defaults to the
	 * current path and query. Not cached: every call asks the server. Rejects on failure, after also setting `error`.
	 */
	checkStatus: (returnUrl?: string) => Promise<AuthStatus>;
}

export const useAuthStatus = (): UseAuthStatusResult => {
	const status = ref<AuthStatus | null>(null);
	const loading = ref(false);
	const error = ref<string | null>(null);
	let pendingChecks = 0;

	const checkStatus = async (returnUrl?: string): Promise<AuthStatus> => {
		pendingChecks += 1;
		loading.value = true;
		error.value = null;
		try {
			const effectiveReturnUrl = returnUrl ?? getCurrentReturnUrl();
			if (!isLocalReturnUrl(effectiveReturnUrl)) {
				throw new Error('Return URL must be a local path starting with "/" (not "//" or "/\\")');
			}
			// `scheme` is deliberately not sent: the CMS resolves it from the request host.
			const query = new URLSearchParams({ returnUrl: effectiveReturnUrl });
			// `baseUrl` is set from the page in App.vue; like the existing auth store, append the path to it.
			const response = await fetch(`${defaultConfig.baseUrl}${TOKEN_EXPIRE_ENDPOINT}?${query.toString()}`, {
				method: 'GET',
				credentials: 'include',
				cache: 'no-store',
				headers: { Accept: 'application/json' },
			});
			if (!response.ok) {
				throw new Error(`Login status request failed with HTTP ${response.status}`);
			}
			const result = (await response.json()) as AuthStatus;
			status.value = result;
			return result;
		} catch (caught) {
			error.value = caught instanceof Error ? caught.message : String(caught);
			throw caught;
		} finally {
			pendingChecks -= 1;
			loading.value = pendingChecks > 0;
		}
	};

	return { status, loading, error, checkStatus };
};
